import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import type { VenueCampaignSummaryItem } from '@/lib/venue/campaignTypes'
import { CAMPAIGN_STATUS_TONE, readRateText } from '@/lib/venue/campaignViewModel'
import { formatDateTime } from '@/lib/venue/format'

/**
 * 이력 목록 표(요구사항 "목록(제목·발송일시·대상 요약·발송 건수·읽음 수/읽음률)") + "더 보기" 페이지네이션.
 *
 * 🚨 `onSelect`는 `campaignId`가 아니라 행 전체(`VenueCampaignSummaryItem`)를 넘긴다(#732) — 호출부가
 * `campaignRowPath`로 PROMOTION+DRAFT는 "이어서 작성" 편집 경로, 나머지는 기존 상세 경로로 분기한다.
 */
export function NoticeCampaignListTable({
  items,
  onSelect,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  items: VenueCampaignSummaryItem[]
  onSelect: (item: VenueCampaignSummaryItem) => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}) {
  if (items.length === 0) {
    return <EmptyState title="조회된 발송 이력이 없습니다" />
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-xl border border-slateish-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slateish-200 bg-slateish-50 text-left text-xs font-medium text-slateish-600">
              <th className="px-3 py-2">제목</th>
              <th className="px-3 py-2">유형</th>
              <th className="px-3 py-2">대상</th>
              <th className="px-3 py-2">발송일시</th>
              <th className="px-3 py-2">발송 건수</th>
              <th className="px-3 py-2">읽음(수/율)</th>
              <th className="px-3 py-2">상태</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => {
                  onSelect(item)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onSelect(item)
                }}
                className="cursor-pointer border-b border-slateish-200 last:border-b-0 hover:bg-brand-50/40"
              >
                <td className="px-3 py-2">{item.title}</td>
                <td className="px-3 py-2">{item.typeLabel}</td>
                <td className="px-3 py-2">{item.targetTypeLabel}</td>
                <td className="px-3 py-2">{formatDateTime(item.sentAt)}</td>
                <td className="px-3 py-2">{item.successCount.toLocaleString('ko-KR')}</td>
                <td className="px-3 py-2">
                  {item.readCount.toLocaleString('ko-KR')} / {readRateText(item.readRatePercent)}
                </td>
                <td className="px-3 py-2">
                  <Badge tone={CAMPAIGN_STATUS_TONE[item.status]}>{item.statusLabel}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasNextPage ? (
        <div className="flex justify-center">
          <Button variant="secondary" size="sm" disabled={isFetchingNextPage} onClick={onLoadMore}>
            {isFetchingNextPage ? '불러오는 중' : '더 보기'}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
